import { useEffect, useState } from "react";
import { getAccount, getPositions, isAlpacaConfigured, Account, Position } from "../services/alpacaService";

export default function AlpacaDashboard() {
  const [account, setAccount] = useState<Account | null>(null);
  const [positions, setPositions] = useState<Position[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!isAlpacaConfigured()) {
      setError("Alpaca API not configured. Add keys to .env.local");
      setLoading(false);
      return;
    }

    const load = async () => {
      setLoading(true);
      const [acc, pos] = await Promise.all([getAccount(), getPositions()]);
      setAccount(acc);
      setPositions(pos);
      setLoading(false);
    };

    load();
    const interval = setInterval(load, 30000); // refresh every 30s
    return () => clearInterval(interval);
  }, []);

  if (loading) {
    return (
      <div className="bg-gray-800 rounded-xl border border-gray-700 p-6">
        <h2 className="text-lg font-bold text-white mb-4">📈 Alpaca Trading</h2>
        <p className="text-gray-400">Loading account...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-gray-800 rounded-xl border border-gray-700 p-6">
        <h2 className="text-lg font-bold text-white mb-4">📈 Alpaca Trading</h2>
        <p className="text-yellow-400 text-sm">{error}</p>
        <p className="text-gray-500 text-xs mt-2">
          Add to .env.local:<br />
          REACT_APP_ALPACA_API_KEY=your_key<br />
          REACT_APP_ALPACA_SECRET_KEY=your_secret<br />
          REACT_APP_ALPACA_PAPER=true
        </p>
      </div>
    );
  }

  const formatCurrency = (val: string | number) => {
    const num = typeof val === 'string' ? parseFloat(val) : val;
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(num);
  };

  return (
    <div className="bg-gray-800 rounded-xl border border-gray-700 p-6">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-lg font-bold text-white">📈 Alpaca Trading</h2>
        <span className="text-xs text-green-400 bg-green-900/30 px-2 py-1 rounded">
          {account?.paper ? 'Paper Trading' : 'Live'}
        </span>
      </div>

      {/* Account Summary */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-6">
        <div className="bg-gray-900/50 rounded-lg p-3">
          <p className="text-gray-500 text-xs uppercase">Cash</p>
          <p className="text-white font-bold text-lg">{formatCurrency(account?.cash || '0')}</p>
        </div>
        <div className="bg-gray-900/50 rounded-lg p-3">
          <p className="text-gray-500 text-xs uppercase">Portfolio Value</p>
          <p className="text-white font-bold text-lg">{formatCurrency(account?.portfolio_value || '0')}</p>
        </div>
        <div className="bg-gray-900/50 rounded-lg p-3">
          <p className="text-gray-500 text-xs uppercase">Equity</p>
          <p className="text-white font-bold text-lg">{formatCurrency(account?.equity || '0')}</p>
        </div>
        <div className="bg-gray-900/50 rounded-lg p-3">
          <p className="text-gray-500 text-xs uppercase">Buying Power</p>
          <p className="text-white font-bold text-lg">{formatCurrency(account?.buying_power || '0')}</p>
        </div>
      </div>

      {/* Positions */}
      <div>
        <h3 className="text-sm font-semibold text-gray-300 uppercase tracking-wide mb-3">
          Positions ({positions.length})
        </h3>
        {positions.length === 0 ? (
          <p className="text-gray-500 text-sm">No open positions</p>
        ) : (
          <div className="space-y-2">
            {positions.map((pos) => {
              const pl = Number(pos.unrealized_pl);
              const plpc = Number(pos.unrealized_plpc) * 100;
              return (
                <div
                  key={pos.symbol}
                  className="flex items-center justify-between bg-gray-900/40 rounded-lg p-3 border border-gray-700"
                >
                  <div>
                    <p className="text-white font-medium">{pos.symbol}</p>
                    <p className="text-gray-500 text-xs">
                      {pos.qty} shares @ {formatCurrency(pos.avg_entry_price)}
                    </p>
                  </div>
                  <div className="text-right">
                    <p className="text-white font-medium">{formatCurrency(pos.market_value)}</p>
                    <p className={`text-xs ${pl >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                      {pl >= 0 ? '+' : ''}{formatCurrency(pl)} ({plpc.toFixed(2)}%)
                    </p>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Account Status */}
      {account && (
        <div className="mt-4 pt-4 border-t border-gray-700">
          <div className="flex flex-wrap gap-2">
            {account.trading_blocked && (
              <span className="text-xs text-red-400 bg-red-900/30 px-2 py-1 rounded">Trading Blocked</span>
            )}
            {account.transfers_blocked && (
              <span className="text-xs text-yellow-400 bg-yellow-900/30 px-2 py-1 rounded">Transfers Blocked</span>
            )}
            {account.pattern_day_trader && (
              <span className="text-xs text-blue-400 bg-blue-900/30 px-2 py-1 rounded">Pattern Day Trader</span>
            )}
          </div>
        </div>
      )}
    </div>
  );
}